















































import BaseComponent from '@/components/base-component.vue';
import GroupForm from '@/components/groups/group-form.vue';
import SortableGroupList from '@/components/groups/sortable-group-list.vue';
import SubgroupForm from '@/components/groups/subgroup-form.vue';
import ListMessages from '@/components/UI/list-messages.vue';
import PermissionAuth from '@/components/UI/permission-auth.vue';
import { errorHandling } from '@/helpers/errorHandling';
import IProduct from '@/interfaces/product';
import * as service from '@/services/groups';
import {
  ERROR_CODE_GROUPS_DELETE,
  ERROR_CODE_SUBGROUPS_DELETE
} from '@/settings/consts/api-error';
import {
  MESSAGE_GROUPS_DELETE_ERROR,
  MESSAGE_SUBGROUPS_DELETE_ERROR
} from '@/settings/consts/messages';
import { cloneDeep } from 'lodash';
import { BehaviorSubject, zip } from 'rxjs';
import { skipWhile } from 'rxjs/operators';
import Component from 'vue-class-component';

@Component({
  components: {
    PermissionAuth,
    GroupForm,
    SubgroupForm,
    ListMessages,
    SortableGroupList,
  },
})
export default class Groups extends BaseComponent {
  public error: boolean = false;
  public data: any = [];
  public isFormOpen: boolean = false;
  public isEdit: boolean = false;
  public editItem: any = {
    nome: '',
  };
  public operationType: 'group' | 'subgroup' | '' = '';
  public selectedGroup: any = {};
  public showProducts: boolean = false;
  private saveSubgroup$: BehaviorSubject<any> = new BehaviorSubject(
    'initialize',
  );

  public beforeMount(): void {
    this.load();

    this.saveSubgroup$
      .pipe(skipWhile((val) => val === 'initialize'))
      .subscribe((val) => {
        this._updateOrder();
      });
  }

  public load(): void {
    this.loader.start();
    zip(
      service.getSubgroups(),
      service.getGroups(),
      service.getSubgroupsProductOptions(),
    ).subscribe(
      ([subgroups, groups, products]) => {
        this.loader.stop();
        this.data = this.formatGroups(subgroups, groups, products);
      },
      () => {
        this.loader.stop();
        this.error = true;
      },
    );
  }

  public onChangeSort(event) {
    this.saveSubgroup$.next('reorderGroup');
  }

  public onChangeGroup(element, group) {
    this.selectedGroup = group;
    this._saveSubgroup(element);
  }

  public formatGroups(subgroups: any, groups: any, products: IProduct[]): any {
    const subgroupsOrdered = this._sortItems(subgroups);
    const groupsOrdered = this._sortItems(groups);
    const productsOrdered = this._sortItems(products);

    return groupsOrdered.reduce((acc, cur) => {
      const grupo = { ...cur, subgrupos: [] };
      const subg = subgroupsOrdered
        .filter(this._subgroupsInGroup(cur.id))
        .map(this._assignProductsToSubgroups(productsOrdered));

      if (!!subg.length) {
        grupo.subgrupos.push(...subg);
      }
      acc.push(grupo);
      return acc;
    }, []);
  }

  public onSubgroupCreate(group: any): void {
    this._setOperation('subgroup', group);
    this._setEditMode(false);
    this._openForm();
  }

  public onGroupEdit(item: any): void {
    this._setOperation('group');
    this._setEditMode(cloneDeep(item));
    this._openForm();
  }

  public onGroupRemove(id: string): void {
    const deleteAction = () => {
      const onError = (error) => {
        this.loader.stop();

        const errorMessages = {
          default: MESSAGE_GROUPS_DELETE_ERROR,
          [ERROR_CODE_GROUPS_DELETE]:
            !!error && !!error.data
              ? error.data.message
              : MESSAGE_GROUPS_DELETE_ERROR,
        };

        const message = errorHandling(error, errorMessages);
        this.toast.show(message, 'error');
      };

      this.loader.start('Excluindo...');
      service.deleteGroup(id).subscribe((data) => {
        this.loader.stop();
        this.load();
      }, onError);
    };

    this.dialog.show(
      'error',
      'Excluir Grupo',
      'Tem certeza que deseja excluir este grupo e todos seus subgrupos?',
      'Cancelar',
      '',
      'Excluir',
      deleteAction,
    );
  }

  public onSubgroupEdit(item: any, group: any): void {
    this._setOperation('subgroup', group);
    this._setEditMode(cloneDeep(item));
    this._openForm();
  }

  public onSubgroupRemove(id: string): void {
    const deleteAction = () => {
      const onError = (error) => {
        this.loader.stop();

        const errorMessages = {
          default: MESSAGE_SUBGROUPS_DELETE_ERROR,
          [ERROR_CODE_SUBGROUPS_DELETE]:
            !!error && !!error.data
              ? error.data.message
              : MESSAGE_SUBGROUPS_DELETE_ERROR,
        };

        const message = errorHandling(error, errorMessages);
        this.toast.show(message, 'error');
      };

      this.loader.start('Excluindo...');
      service.deleteSubgroup(id).subscribe((data) => {
        this.loader.stop();
        this.load();
      }, onError);
    };

    this.dialog.show(
      'error',
      'Excluir Subgrupo',
      'Tem certeza que deseja excluir este subgrupo?',
      'Cancelar',
      '',
      'Excluir',
      deleteAction,
    );
  }

  public onGroupCreate(): void {
    this._setOperation('group');
    this._setEditMode(false);
    this.editItem = {
      nome: '',
    };
    this._openForm();
  }

  public receiveFormData(payload: any, form: string): void {
    if (form === 'group') {
      return this._saveGroup(payload);
    }
    return this._saveSubgroup(payload);
  }

  public closeForm(): void {
    this._setEditMode(false);
    this.isFormOpen = false;
  }

  private _subgroupsInGroup(id: any) {
    return (i) => !!i.grupo && i.grupo.id === id;
  }

  private _assignProductsToSubgroups(products: any[]) {
    return (subg) => ({
      ...subg,
      products: [
        ...products
          .filter((p) => !!p.subgrupo && p.subgrupo.id === subg.id)
          .map((i, ordem) => ({ ordem, ...i, grupo: subg.grupo })),
      ],
    });
  }
  private _sortItems(items: any[]): any[] {
    return items.sort((itemA, itemB) => {
      if (itemA.ordem < itemB.ordem) {
        return -1;
      }
      if (itemA.ordem > itemB.ordem) {
        return 1;
      }
      return 0;
    });
  }

  private _openForm(): void {
    this.isFormOpen = true;
  }

  private _setOperation(op: 'group' | 'subgroup' | '', group?: any): void {
    this.operationType = op;
    this.selectedGroup = group;
  }

  private _setEditMode(item: any): void {
    if (item === false) {
      this.isEdit = false;
      this.editItem = {
        nome: '',
      };
      return;
    }

    this.editItem = cloneDeep(item);
    this.isEdit = true;
  }

  private _saveGroup(payload: any): void {
    this.loader.start('Salvando...');
    payload.ordem = !!payload.id ? payload.ordem : this.data.length;
    service.saveGroup(payload).subscribe(
      (data) => {
        this.closeForm();
        this.load();
        this.loader.stop();
      },
      (err) => {
        this.loader.stop();
        this.toast.show('Ocorreu um erro ao salvar.', 'error');
      },
    );
  }

  private _saveSubgroup(payload: any): void {
    this.loader.start('Salvando...');
    const order = !!this.selectedGroup.subgrupos
      ? this.selectedGroup.subgrupos.length
      : 0;

    const pld = { ...payload, ordem: order, grupo: this.selectedGroup };

    service.saveSubgroup(pld).subscribe(
      (data) => {
        this.closeForm();
        this.loader.stop();
        this.saveSubgroup$.next('reorderGroup');
      },
      (err) => {
        this.loader.stop();
        this.toast.show('Ocorreu um erro ao salvar.', 'error');
      },
    );
  }

  private _updateOrder(): void {
    this.loader.start('Salvando...');
    const payload = cloneDeep(this.data).map((group) => {
      group.subgrupos = group.subgrupos.map((subgroup) => {
        delete subgroup.grupo;
        return {
          ...subgroup,
          produtos: subgroup.products.map(({ codigo, id, ordem }) => {
            return {
              codigo,
              id,
              ordem,
            };
          }),
        };
      });

      return group;
    });

    service.updateOrderGroup(payload).subscribe(
      (data) => {
        this.closeForm();
        this.load();
        this.loader.stop();
      },
      (err) => {
        this.loader.stop();
        this.toast.show('Ocorreu um erro ao salvar.', 'error');
      },
    );
  }
}
